import type { EvaluationCriteriaEducationYear, EvaluationCriteriaSubjectOption } from 'app/types/evaluationCriteria';

export type CatalogDiscipline = {
  guid: string;
  name?: string;
  discipline?: string;
};

/** Opción del select: el valor es el guid de la disciplina, que es lo que se busca. */
export type DisciplineOption = {
  label: string;
  value: string;
};

type RawEducationYear = Partial<EvaluationCriteriaEducationYear> | null | undefined;

/** Fila tal como puede llegar del back: el contrato no protege de nulos ni de subjects repetidos. */
type RawSubjectOptionRow = {
  subject?: string | null;
  disciplineIds?: Array<string | null | undefined> | null;
  educationYears?: RawEducationYear[] | null;
};

export type RawSubjectOption = RawSubjectOptionRow | null | undefined;

type ResolveSelectedDisciplineParams = {
  currentDisciplineId: string | null;
  preselectedDisciplineId?: string;
  subjects: EvaluationCriteriaSubjectOption[];
};

function getDisciplineLabel(discipline: CatalogDiscipline): string {
  return discipline?.discipline ?? discipline?.name ?? '';
}

function isSameEducationYear(a: EvaluationCriteriaEducationYear, b: EvaluationCriteriaEducationYear): boolean {
  return a.educationLevel === b.educationLevel && a.educationYear === b.educationYear;
}

function addDisciplineIds(target: string[], ids: RawSubjectOptionRow['disciplineIds']): void {
  (ids ?? []).forEach((id) => {
    if (id && !target.includes(id)) target.push(id);
  });
}

// Sin filtrar por etapa: solo hay criterios de Primaria, Secundaria y Bachillerato, y es el back el que decide qué
// cursos tienen criterios. Un filtro aquí obligaría a mantener los ids de etapa de cada catálogo de colegio.
function addEducationYears(target: EvaluationCriteriaEducationYear[], pairs: RawEducationYear[] | null | undefined): void {
  (pairs ?? []).forEach((pair) => {
    if (!pair?.educationLevel || !pair?.educationYear) return;
    const next = { educationLevel: pair.educationLevel, educationYear: pair.educationYear };
    if (!target.some((existing) => isSameEducationYear(existing, next))) target.push(next);
  });
}

/**
 * Deja la respuesta del back lista para el resto de funciones: un subject por elemento (los repetidos se fusionan),
 * sin nulos ni repetidos. Un subject sin cursos no sale.
 */
export function normalizeSubjects(raw: RawSubjectOption[] | null | undefined): EvaluationCriteriaSubjectOption[] {
  const bySubject = new Map<string, EvaluationCriteriaSubjectOption>();
  (raw ?? []).forEach((item) => {
    if (!item?.subject) return;
    const merged = bySubject.get(item.subject) ?? { subject: item.subject, disciplineIds: [], educationYears: [] };
    addDisciplineIds(merged.disciplineIds, item.disciplineIds);
    addEducationYears(merged.educationYears, item.educationYears);
    bySubject.set(item.subject, merged);
  });
  return Array.from(bySubject.values()).filter((item) => item.educationYears.length > 0);
}

// El back resuelve un solo subject por disciplina; si un guid llegara en varios, vale el primero en el que aparece.
function findSubjectOfDiscipline(subjects: EvaluationCriteriaSubjectOption[], disciplineId: string): EvaluationCriteriaSubjectOption | null {
  if (!disciplineId) return null;
  return subjects.find((item) => item.disciplineIds.includes(disciplineId)) ?? null;
}

/**
 * Una opción por disciplina de los subjects de la comunidad que exista en el catálogo del tenant, con su nombre. Dos
 * disciplinas con el mismo nombre salen las dos: son distintas y el back las resuelve por guid.
 */
export function getDisciplineOptions(subjects: EvaluationCriteriaSubjectOption[], catalog: CatalogDiscipline[] | null | undefined): DisciplineOption[] {
  const catalogById = new Map((catalog ?? []).filter((discipline) => discipline?.guid).map((discipline) => [discipline.guid, discipline]));
  const disciplineIds = new Set(subjects.flatMap((item) => item.disciplineIds));

  return Array.from(disciplineIds)
    .reduce<DisciplineOption[]>((options, id) => {
      const discipline = catalogById.get(id);
      return discipline ? [...options, { label: getDisciplineLabel(discipline), value: id }] : options;
    }, [])
    .sort((a, b) => a.label.localeCompare(b.label));
}

/** Cursos del subject de la disciplina: dos disciplinas del mismo subject comparten cursos. */
export function getAllowedEducationYears(subjects: EvaluationCriteriaSubjectOption[], disciplineId: string): EvaluationCriteriaEducationYear[] {
  return findSubjectOfDiscipline(subjects, disciplineId)?.educationYears ?? [];
}

/** Etapa y curso se comparan juntos: el id de curso se repite entre etapas. */
export function isEducationYearAllowed(
  allowed: EvaluationCriteriaEducationYear[],
  value: Partial<EvaluationCriteriaEducationYear> | null | undefined
): boolean {
  if (!value?.educationLevel || !value?.educationYear) return false;
  return allowed.some((pair) => isSameEducationYear(pair, value as EvaluationCriteriaEducationYear));
}

/**
 * Disciplina elegida en la comunidad, decidida solo con su respuesta (no con el catálogo, que puede llegar tarde y
 * solo pone la etiqueta). `currentDisciplineId` es `null` mientras el usuario no ha elegido nada: entonces vale la
 * disciplina preseleccionada en el formulario.
 */
export function resolveSelectedDiscipline({ currentDisciplineId, preselectedDisciplineId, subjects }: ResolveSelectedDisciplineParams): string {
  const candidate = currentDisciplineId ?? preselectedDisciplineId ?? '';
  return findSubjectOfDiscipline(subjects, candidate) ? candidate : '';
}
